/**
 * Vérification d'une écriture de forme : la forme RELUE dans le fichier exporté
 * après l'écriture, confrontée à ce que l'agent a demandé (spec #1858, règle
 * commune n°4 ; #1863).
 *
 * WHY un module à part : l'accusé de l'éditeur a déjà dit « réussi » sur une
 * forme absente de l'écran. Le reçu ne dit donc « conforme » que sur ce que le
 * lecteur de forme a relu, réglage par réglage ; ce qu'il n'a pas pu relire est
 * dit « non lisible », jamais conforme. La décision est pure — elle ne connaît
 * ni l'éditeur ni le navigateur — pour se tester sur le fichier de référence.
 */

import {
  CANVAS_FORMAT_DIFFERENCE_EXAMPLES_MAX,
  CANVAS_FORMAT_DIFFERENCE_TEXT_MAX,
  CANVAS_FORMAT_REMAINING_MAX,
  CANVAS_TEXT_FORMAT_SETTING_KEYS,
  type CanvasFormatSettingReceipt,
  type CanvasFormatSettingValue,
  type CanvasFormatUnreadableReason,
  type CanvasTextFormatSettingKey,
} from '@hector/shared-types';
import type { ActeVise, FormeDeLActe, FormeDeParagraphe, FormeDePassage } from '@hector/docx-forme';
import { ecritureDuReglage, jugerLeReglage, passageLuPour, type EcritureDeReglage } from './reglagesDeTexte';

/** Les réglages de texte demandés par l'agent, normalisés (`normaliserLaDemande`) : la forme que rend le lecteur. */
export type ReglagesDeTexte = Partial<Record<CanvasTextFormatSettingKey, CanvasFormatSettingValue>>;

/**
 * Une plage du texte d'un paragraphe, par sa POSITION dans le texte que rend le
 * lecteur de forme (cumul des longueurs de ses passages), avec le texte qu'elle
 * porte. La position distingue deux passages de même texte ; le texte prouve,
 * à la relecture, que la plage désigne encore ce qu'elle désignait.
 */
export interface PlageDeTexte {
  debut: number;
  /** Exclue. */
  fin: number;
  texte: string;
}

/** Ce qui a été écrit dans un paragraphe : tout son texte, un passage désigné par son texte, ou une plage. */
export interface EtendueEcrite {
  nodeId: string;
  /** Absent : tout le texte du paragraphe. */
  passage?: string;
  /** Une plage située par sa position (`onlyWhere`, #1865). L'emporte sur `passage`. */
  plage?: PlageDeTexte;
}

/**
 * Ce que la relecture a rendu : la forme du fichier exporté après l'écriture,
 * ou la raison pour laquelle ce fichier ne peut pas juger l'écriture — export
 * ou lecture en échec, document qui a changé pendant la relecture, ou révision
 * que l'éditeur n'a pas rendue.
 */
export type RelectureDeLActe = { acte: ActeVise } | FichierQuiNeJugeRien;

/** Pourquoi le fichier relu ne peut pas juger l'écriture. */
type FichierQuiNeJugeRien = { nonRelu: Extract<CanvasFormatUnreadableReason, 'reread_failed' | 'changed_during_reread' | 'revision_unavailable'> };

/**
 * Le fichier relu après l'écriture, tel que le lecteur le rend — avant que la
 * manière d'écrire n'en tire ce que la cible visait ({@link RelectureDeLActe}).
 */
export type RelectureDuFichier = { acte: FormeDeLActe } | FichierQuiNeJugeRien;

/**
 * Nombre d'occurrences de `passage` dans `texte`, à la casse près,
 * chevauchements compris : deux occurrences qui se chevauchent ne désignent
 * toujours pas UN passage.
 */
export function occurrencesDuPassage(texte: string, passage: string): number {
  if (passage.length === 0) return 0;
  let compte = 0;
  for (let position = texte.indexOf(passage); position >= 0; position = texte.indexOf(passage, position + 1)) compte += 1;
  return compte;
}

/** La part d'un passage lu que l'étendue écrite recouvre. */
interface PartRelue {
  lu: FormeDePassage;
  texte: string;
  /** Le style nommé du paragraphe : ce qu'un retrait conforme laisse vient de lui. */
  styleNom: string | null;
}

/**
 * Les passages lus que l'étendue recouvre, ou `null` si elle n'est plus dans le
 * fichier relu (paragraphe absent, sans texte, passage introuvable ou devenu
 * multiple, ou plage dont le texte n'est plus celui visé).
 */
function partsRelues(acte: ActeVise, etendue: EtendueEcrite): PartRelue[] | null {
  return partsDuParagraphe(acte.paragraphes.find((lu) => lu.nodeId === etendue.nodeId), etendue);
}

/** Les mêmes parts, dans le paragraphe déjà retrouvé ; `undefined` : il n'est pas dans le fichier. */
function partsDuParagraphe(paragraphe: FormeDeParagraphe | undefined, etendue: Pick<EtendueEcrite, 'passage' | 'plage'>): PartRelue[] | null {
  if (paragraphe === undefined || paragraphe.passages.length === 0) return null;
  const { styleNom } = paragraphe;
  const { passage, plage } = etendue;

  const texte = paragraphe.passages.map((lu) => lu.texte).join('');
  let debut: number;
  let fin: number;
  if (plage !== undefined) {
    ({ debut, fin } = plage);
    // Le texte du paragraphe a changé sous la plage : elle ne désigne plus ce qui a été visé.
    if (texte.slice(debut, fin) !== plage.texte) return null;
  } else if (passage !== undefined) {
    if (occurrencesDuPassage(texte, passage) !== 1) return null;
    debut = texte.indexOf(passage);
    fin = debut + passage.length;
  } else {
    return paragraphe.passages.map((lu) => ({ lu, texte: lu.texte, styleNom }));
  }
  const parts: PartRelue[] = [];
  let curseur = 0;
  for (const lu of paragraphe.passages) {
    const [de, a] = [Math.max(debut, curseur), Math.min(fin, curseur + lu.texte.length)];
    if (de < a) parts.push({ lu, texte: lu.texte.slice(de - curseur, a - curseur), styleNom });
    curseur += lu.texte.length;
  }
  return parts;
}

/** Ce que l'éditeur reçoit pour une étendue : l'`inline` de son étape, et si l'écrire n'y changerait rien. */
export interface EcritureSurEtendue {
  /** Tous les réglages demandés, aux clés et unités de l'éditeur (`reglagesDeTexte.ts`). */
  inline: EcritureDeReglage['inline'];
  /** Vrai si AUCUN réglage ne changerait quoi que ce soit sur cette étendue. */
  sansEffet: boolean;
}

/**
 * Ce que l'éditeur doit recevoir pour poser `demandes` sur `etendue`, d'après
 * la forme lue AVANT l'écriture, et si cette écriture y serait sans effet.
 *
 * WHY : l'éditeur refuse en bloc un plan atomique dont une étape ne change rien
 * (`NO_OP`, jeté — sondes `mesure-format-text-cibles` N1 à N4 et
 * `mesure-format-text-police-taille-couleur`). Une étendue où rien ne
 * changerait est donc laissée hors du plan ; la relecture la juge avec les
 * autres. Une étape est acceptée dès qu'UN de ses réglages change quelque
 * chose : elle porte alors tous les réglages demandés.
 *
 * Une étendue introuvable (paragraphe ou passage) n'est jamais dite sans
 * effet : sans lecture, rien ne le prouve. Un réglage dont la VALEUR ne se lit
 * pas reste, lui, jugé sur ce que le lecteur sait de sa forme directe
 * (`reglagesDeTexte.ts`).
 */
export function ecritureSurLEtendue(acte: ActeVise, etendue: EtendueEcrite, demandes: ReglagesDeTexte): EcritureSurEtendue {
  return ecritureSurLesParts(partsRelues(acte, etendue), demandes);
}

/** {@link ecritureSurLEtendue}, pour tout le texte d'UN paragraphe déjà lu. */
export function ecritureSurLeParagraphe(paragraphe: FormeDeParagraphe, demandes: ReglagesDeTexte): EcritureSurEtendue {
  return ecritureSurLesParts(partsDuParagraphe(paragraphe, {}), demandes);
}

function ecritureSurLesParts(parts: PartRelue[] | null, demandes: ReglagesDeTexte): EcritureSurEtendue {
  const ecriture: EcritureSurEtendue = { inline: {}, sansEffet: parts !== null };
  for (const setting of CANVAS_TEXT_FORMAT_SETTING_KEYS) {
    const demande = demandes[setting];
    if (demande === undefined) continue;
    const reglage = ecritureDuReglage(setting, demande, (parts ?? []).map(({ lu }) => passageLuPour(lu, setting)));
    Object.assign(ecriture.inline, reglage.inline);
    ecriture.sansEffet &&= reglage.sansEffet;
  }
  return ecriture;
}

/** Une forme que le texte porte encore après le retrait conforme d'un réglage de TEXTE, telle que le reçu la nomme : comptée en passages. */
type FormeRestante = Extract<NonNullable<CanvasFormatSettingReceipt['remaining']>[number], { passages: number }>;

/** Ce que le fichier relu dit d'UN réglage sur UNE étendue écrite. */
export interface JugementDEtendue {
  /** Les parts de l'étendue qui portent une autre valeur que la demande, avec la valeur lue. */
  ecarts: Array<{ nodeId: string; text: string; read: CanvasFormatSettingValue }>;
  /** Ce qui n'a pas pu être jugé : l'étendue entière, ou celles de ses parts dont la valeur ne se lit pas. */
  nonLus: number;
  /** Pourquoi, quand `nonLus` n'est pas nul. */
  raison?: CanvasFormatUnreadableReason;
  /** Après un retrait conforme, ce que chaque part porte encore. */
  restes: FormeRestante[];
}

/**
 * Juge UN réglage sur UNE étendue. Ce que « conforme » veut dire pour ce
 * réglage — une valeur, ou une origine quand on le retire — est dit par
 * `jugerLeReglage`.
 *
 * @param relecture La forme relue dans le fichier exporté APRÈS l'écriture, ou
 *   la raison pour laquelle ce fichier ne juge pas l'écriture : l'étendue n'est
 *   alors dite ni conforme ni différente.
 */
export function jugerLEtendue(
  relecture: RelectureDeLActe,
  etendue: EtendueEcrite,
  setting: CanvasTextFormatSettingKey,
  requested: CanvasFormatSettingValue,
): JugementDEtendue {
  if ('nonRelu' in relecture) return { ecarts: [], nonLus: 1, raison: relecture.nonRelu, restes: [] };
  const parts = partsRelues(relecture.acte, etendue);
  if (parts === null) return { ecarts: [], nonLus: 1, raison: 'target_not_found_on_reread', restes: [] };
  const jugement: JugementDEtendue = { ecarts: [], nonLus: 0, restes: [] };
  for (const { lu, texte, styleNom } of parts) {
    const verdict = jugerLeReglage(setting, requested, passageLuPour(lu, setting));
    if (verdict.issue === 'non_lisible') {
      jugement.nonLus += 1;
      jugement.raison ??= 'cascade_not_resolved';
    } else if (verdict.issue !== 'conforme') {
      jugement.ecarts.push({ nodeId: etendue.nodeId, text: texte.slice(0, CANVAS_FORMAT_DIFFERENCE_TEXT_MAX), read: verdict.lu });
    } else if (verdict.reste !== undefined) {
      const { reste } = verdict;
      jugement.restes.push({
        ...('valeur' in reste ? { read: reste.valeur } : {}),
        origin: reste.origine,
        // Le style du paragraphe est nommé dès que ce qui reste vient d'un style : c'est le plus souvent
        // lui qui le donne, mais ce peut être un style de caractère, de tableau ou un niveau de liste.
        ...(reste.origine === 'style' && styleNom !== null ? { paragraphStyleName: styleNom } : {}),
        passages: 1,
      });
    }
  }
  return jugement;
}

/**
 * Le reçu d'UN réglage, d'après le jugement de chacune des étendues écrites.
 * Après un retrait conforme, `remaining` dit ce que le texte porte encore,
 * regroupé par valeur, origine et style nommé, du plus fréquent au moins
 * fréquent ; au-delà de sa borne, `remainingNotListed` compte les formes qu'il
 * ne liste pas.
 *
 * @param jugements Un jugement par étendue, dans l'ordre du document.
 * @param paragraphesNonEcrits Paragraphes visés que l'éditeur n'a pas écrits :
 *   non relus, comptés non jugés (`not_written`).
 */
export function recuDuReglage(
  setting: CanvasTextFormatSettingKey,
  requested: CanvasFormatSettingValue,
  jugements: readonly JugementDEtendue[],
  paragraphesNonEcrits = 0,
): CanvasFormatSettingReceipt {
  const ecarts = jugements.flatMap((jugement) => jugement.ecarts);
  let nonLus = jugements.reduce((somme, jugement) => somme + jugement.nonLus, 0);
  let raison = jugements.find((jugement) => jugement.raison !== undefined)?.raison;
  if (paragraphesNonEcrits > 0) {
    nonLus += paragraphesNonEcrits;
    raison ??= 'not_written';
  }
  const restes = new Map<string, FormeRestante>();
  for (const forme of jugements.flatMap((jugement) => jugement.restes)) {
    const cle = JSON.stringify([forme.read, forme.origin, forme.paragraphStyleName]);
    const connue = restes.get(cle);
    if (connue === undefined) restes.set(cle, { ...forme });
    else connue.passages += forme.passages;
  }
  const [premier, ...autres] = ecarts.slice(0, CANVAS_FORMAT_DIFFERENCE_EXAMPLES_MAX);
  const [premierReste, ...autresRestes] = [...restes.values()].sort((a, b) => b.passages - a.passages).slice(0, CANVAS_FORMAT_REMAINING_MAX);
  const formesNonListees = restes.size - CANVAS_FORMAT_REMAINING_MAX;
  return {
    setting,
    requested,
    outcome: ecarts.length > 0 ? 'different' : nonLus > 0 ? 'unreadable' : 'conforming',
    ...(premier === undefined ? {} : { different: { count: ecarts.length, examples: [premier, ...autres] } }),
    ...(raison === undefined ? {} : { unreadable: { count: nonLus, reason: raison } }),
    ...(premierReste === undefined ? {} : { remaining: [premierReste, ...autresRestes] }),
    ...(formesNonListees > 0 ? { remainingNotListed: formesNonListees } : {}),
  };
}

/**
 * Juge, réglage par réglage, ce que le fichier relu porte sur les étendues
 * écrites ({@link jugerLEtendue}), et en rend le reçu ({@link recuDuReglage}).
 *
 * @param relecture La forme relue dans le fichier exporté APRÈS l'écriture, ou
 *   la raison pour laquelle ce fichier ne juge pas l'écriture : aucun réglage
 *   n'est alors dit conforme ni différent.
 * @param etendues Ce que la cible désigne et que l'éditeur a écrit (ou qui
 *   portait déjà la forme), dans l'ordre du document.
 * @param demandes Les réglages demandés. Les réglages absents ne sont pas jugés.
 * @param paragraphesNonEcrits Paragraphes visés que l'éditeur n'a pas écrits
 *   (plan refusé après d'autres plans réussis). Ils ne sont pas relus : chaque
 *   réglage les compte comme non jugés (`not_written`), donc aucun ne sort
 *   conforme pour la demande entière.
 * @returns Un reçu par réglage demandé, dans l'ordre du contrat partagé.
 */
export function verifierLaFormeEcrite(
  relecture: RelectureDeLActe,
  etendues: readonly EtendueEcrite[],
  demandes: ReglagesDeTexte,
  paragraphesNonEcrits = 0,
): CanvasFormatSettingReceipt[] {
  return CANVAS_TEXT_FORMAT_SETTING_KEYS.flatMap((setting): CanvasFormatSettingReceipt[] => {
    const requested = demandes[setting];
    if (requested === undefined) return [];
    return [recuDuReglage(setting, requested, etendues.map((etendue) => jugerLEtendue(relecture, etendue, setting, requested)), paragraphesNonEcrits)];
  });
}
